import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarCheck, ChevronLeft, ChevronRight, Clock, Youtube } from 'lucide-react';
import PublicLayout, { Eyebrow, usePublicMeta } from './PublicLayout';
import YouTubeVideo from './YouTubeVideo';
import { SCHOOL_TUTORIALS, SCHOOL_TUTORIALS_PLAYLIST, SCHOOL_TUTORIAL_LIST, totalMinutes } from './site';

const TITLE = 'YADA for Schools tutorials · Learn the school panel';
const DESCRIPTION =
  'Short narrated videos that walk school directors, teachers and counsellors through the YADA School panel, ' +
  'from the first sign-in to exams, paper answer sheets, alerts and the AI coach.';

const COUNT = SCHOOL_TUTORIAL_LIST.length;

export default function Tutorials() {
  usePublicMeta(TITLE, DESCRIPTION);
  const [params, setParams] = useSearchParams();
  const asked = Number(params.get('v'));
  const n = Number.isInteger(asked) && asked >= 1 && asked <= COUNT ? asked : 1;
  const video = SCHOOL_TUTORIAL_LIST[n - 1];
  // Start playing on its own only after the visitor has picked a video here.
  const [picked, setPicked] = useState(false);
  const playerRef = useRef<HTMLDivElement>(null);

  const go = (to: number) => {
    setPicked(true);
    setParams({ v: String(to) }, { replace: true });
    if (playerRef.current && playerRef.current.getBoundingClientRect().top < 0) {
      playerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <PublicLayout>
      <section className="relative overflow-hidden bg-navy text-white">
        <div aria-hidden className="pointer-events-none absolute -left-40 -top-48 h-[480px] w-[480px] rounded-full bg-slate/30 blur-3xl" />
        <div className="relative mx-auto max-w-[1200px] px-4 pb-14 pt-12 sm:px-6 lg:pb-16 lg:pt-16">
          <Eyebrow onNavy>YADA for Schools · Tutorials</Eyebrow>
          <h1 className="mt-4 max-w-[820px] font-display text-[36px] font-extrabold leading-[1.05] tracking-[-0.035em] sm:text-[50px]">
            Learn the YADA School panel in {COUNT} short videos.
          </h1>
          <p className="mt-4 max-w-[640px] text-[17px] leading-relaxed text-on-navy">
            Each video follows one job at a real school, step by step, with narration and subtitles.
            Watch them in order with your staff, or jump to the part you need today.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-on-navy">
            <span className="inline-flex items-center gap-2"><Clock size={16} className="text-accent" />
              About {totalMinutes(SCHOOL_TUTORIAL_LIST)} minutes in all</span>
            <a href={SCHOOL_TUTORIALS_PLAYLIST} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-semibold text-white no-underline hover:text-accent">
              <Youtube size={17} /> Watch the playlist on YouTube
            </a>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 lg:py-14">
        <div className="grid items-start gap-8 lg:grid-cols-[1.65fr_1fr]">
          <div ref={playerRef} className="scroll-mt-20 lg:sticky lg:top-24">
            <YouTubeVideo key={video.id} id={video.id} title={video.title} duration={video.duration}
              autoPlay={picked} large className="shadow-[0_30px_60px_-30px_rgba(10,25,35,.7)]" />
            <div className="mt-5 font-mono text-[11.5px] font-semibold uppercase tracking-[0.08em] text-accent-ink">
              Part {video.n} of {COUNT} · {video.stage}
            </div>
            <h2 className="mt-1.5 font-display text-[26px] font-extrabold leading-tight tracking-[-0.02em] text-ink sm:text-[30px]">
              {video.title}
            </h2>
            <p className="mt-2 max-w-[640px] text-[16px] leading-relaxed text-muted">{video.summary}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" onClick={() => go(n - 1)} disabled={n === 1}
                className="inline-flex items-center gap-1.5 rounded-[12px] border border-line bg-surface px-4 py-2.5 text-sm font-semibold text-ink transition hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-40">
                <ChevronLeft size={16} /> Previous
              </button>
              <button type="button" onClick={() => go(n + 1)} disabled={n === COUNT}
                className="inline-flex items-center gap-1.5 rounded-[12px] bg-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-navy-2 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-slate">
                Next video <ChevronRight size={16} />
              </button>
            </div>
          </div>

          <nav aria-label="Tutorial videos" className="rounded-[20px] border border-line bg-surface p-3 shadow-card sm:p-4">
            {SCHOOL_TUTORIALS.map((stage) => (
              <div key={stage.title} className="mb-3 last:mb-0">
                <div className="flex items-baseline justify-between px-2 pb-2 pt-2">
                  <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">{stage.title}</h3>
                  <span className="font-mono text-[11px] text-muted-2">{totalMinutes(stage.videos)} min</span>
                </div>
                <ol className="m-0 list-none space-y-1 p-0">
                  {stage.videos.map((v) => {
                    const item = SCHOOL_TUTORIAL_LIST.find((t) => t.id === v.id)!;
                    const current = item.n === n;
                    return (
                      <li key={v.id}>
                        <button type="button" onClick={() => go(item.n)} aria-current={current ? 'true' : undefined}
                          className={`flex w-full items-center justify-start gap-3 rounded-[12px] p-2 text-left transition ${current ? 'bg-accent/15 ring-1 ring-accent/50' : 'hover:bg-surface-2'}`}>
                          <span className="relative block aspect-video w-[104px] shrink-0 overflow-hidden rounded-[8px] bg-navy-deep">
                            <img src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`} alt="" loading="lazy"
                              className="absolute inset-0 h-full w-full object-cover" />
                            <span className="absolute bottom-1 right-1 rounded-[5px] bg-navy-deep/85 px-1 font-mono text-[10.5px] font-semibold text-white">{v.duration}</span>
                          </span>
                          <span className="min-w-0">
                            <span className={`block font-mono text-[11px] font-semibold ${current ? 'text-accent-ink' : 'text-muted'}`}>
                              {current ? `Now playing · ${item.n}` : `Part ${item.n}`}
                            </span>
                            <span className="mt-0.5 block text-[14.5px] font-semibold leading-snug text-ink">{v.title}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </div>
            ))}
          </nav>
        </div>
      </section>

      <section className="border-t border-line bg-surface">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-6 px-4 py-14 sm:px-6">
          <div>
            <h2 className="font-display text-[28px] font-extrabold tracking-[-0.02em] text-ink">Want to see it with your own school’s data?</h2>
            <p className="mt-2 max-w-[560px] text-muted">
              We set up a trial for your school and walk your staff through it in person or online.
            </p>
          </div>
          <a href="/#demo"
            className="inline-flex items-center gap-2 rounded-[14px] bg-accent px-5 py-[15px] font-semibold text-white no-underline hover:brightness-105">
            <CalendarCheck size={18} /> Book a demo
          </a>
        </div>
      </section>
    </PublicLayout>
  );
}
